/**
 * ChapterProgressView — the presentational body of the Chapter Progress What's
 * New experience. Pure: it takes ready ChapterStat data + callbacks and renders,
 * so the same component backs the live student experience AND the admin "preview
 * in the context of any user". No data fetching, no navigation, no app coupling.
 *
 * See docs/FeatureDesigns/whats-new-chapter-progress.md.
 */
import { accuracyTier, ACCURACY_TIER_COLOR, MIN_ATTEMPTS_FOR_STATS, type ChapterStat } from './chapterProgress';
import './ChapterProgressView.css';

export interface ChapterProgressViewProps {
    subjectLabel: string;
    status: 'loading' | 'error' | 'ready';
    chapters: ChapterStat[];
    /** Primary CTA — go practise the subject. */
    onPractice: () => void;
    /** Secondary defer control. Omit to hide (e.g. admin preview). */
    onLater?: () => void;
}

function Ring({ score, size = 44 }: { score: number; size?: number }) {
    const color = ACCURACY_TIER_COLOR[accuracyTier(score)];
    const deg = Math.round((score / 10) * 360);
    const inner = Math.round(size * 0.74);
    return (
        <span
            className="cpx-ring"
            style={{ width: size, height: size, background: `conic-gradient(${color} ${deg}deg, var(--bg-tertiary) 0)` }}
        >
            <span className="cpx-ring-inner" style={{ width: inner, height: inner }}>{score.toFixed(1)}</span>
        </span>
    );
}

export function ChapterProgressView({ subjectLabel, status, chapters, onPractice, onLater }: ChapterProgressViewProps) {
    if (status === 'loading') {
        return <div className="cpx"><p className="cpx-loading">Crunching your {subjectLabel} stats…</p></div>;
    }

    if (status === 'error' || chapters.length === 0) {
        return (
            <div className="cpx">
                <p className="cpx-empty-title">Let's get your {subjectLabel} journey started 🚀</p>
                <p className="cpx-empty-sub">Practise a few questions and you'll see your chapter-by-chapter progress here.</p>
                <div className="cpx-actions">
                    <button type="button" className="cpx-cta" onClick={onPractice}>Practise {subjectLabel} →</button>
                    {onLater && <button type="button" className="cpx-later" onClick={onLater}>Maybe later</button>}
                </div>
            </div>
        );
    }

    const top = chapters[0];
    const lowest = chapters[chapters.length - 1];
    const showNudge = chapters.length >= 2; // distinct weakest chapter to nudge on

    return (
        <div className="cpx">
            <div className="cpx-banner cpx-banner--praise">
                <span className="cpx-banner-emoji">🎉</span>
                <span>
                    You're strongest in <strong>{top.name}</strong> — {top.accuracy.toFixed(1)}/10 accuracy. Brilliant work!
                </span>
            </div>

            <ul className="cpx-list">
                {chapters.map(c => (
                    <li key={c.id} className="cpx-row">
                        <Ring score={c.accuracy} />
                        <div className="cpx-row-main">
                            <div className="cpx-row-name">{c.name}</div>
                            <div className="cpx-row-meta">
                                {c.approach != null && <span>Approach <strong>{c.approach.toFixed(1)}</strong></span>}
                            </div>
                        </div>
                        <div className="cpx-row-count">Attempted <strong>{c.attempted}</strong> (total <strong>{c.total}</strong>)</div>
                    </li>
                ))}
            </ul>

            {showNudge && (
                <div className="cpx-banner cpx-banner--nudge">
                    <span className="cpx-banner-emoji">💪</span>
                    <span>
                        A little more on <strong>{lowest.name}</strong> ({lowest.accuracy.toFixed(1)}/10) will round out your {subjectLabel}. Let's give it a go!
                    </span>
                </div>
            )}

            <div className="cpx-actions">
                <button type="button" className="cpx-cta" onClick={onPractice}>Practise {subjectLabel} →</button>
                {onLater && <button type="button" className="cpx-later" onClick={onLater}>Maybe later</button>}
            </div>

            <p className="cpx-footnote">Only chapters with {MIN_ATTEMPTS_FOR_STATS} or more questions attempted are shown.</p>
        </div>
    );
}
